import { useMemo, useState } from 'react';
import { MessageSquare, Play, Search } from 'lucide-react';
import type { StartSmsTestBody } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useDocuments } from '@/hooks/use-documents';
import { usePatientSearch } from '@/hooks/use-patients';
import { useSchedulingScope } from '@/scheduling/hooks/use-scheduling-scope';
import { SchedulingScopeFields } from '@/scheduling/components/SchedulingScopeFields';
import { SmsChat } from './SmsChat';
import { useSmsChat } from './use-sms-chat';

type Mode = 'referral' | 'direct';

/**
 * In-product SMS scheduling agent test tool (dev-tools, URL-only at /sms/test).
 *
 * "Start" opens a REAL outbound scheduling conversation for the current tenant
 * (same context the voice agent uses) and drives the real apps/sms-agent service;
 * each reply is a patient turn. A successful conversation books a REAL
 * appointment against the API/EHR. No text is sent — messages appear only here.
 */
export function SmsTestPage(): React.ReactElement {
  const chat = useSmsChat();

  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-muted/30 px-6 py-3">
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <MessageSquare className="h-5 w-5" />
          SMS Scheduling Agent — Test Tool
        </h1>
        <p className="text-xs text-muted-foreground">
          Dev tool. Starts a real outbound scheduling conversation for this practice and books a{' '}
          <span className="font-medium">real</span> appointment. No text is sent — replies are
          simulated here.
        </p>
      </div>

      <div className="min-h-0 flex-1">
        {chat.started ? (
          <SmsChat
            messages={chat.messages}
            isLoading={chat.isLoading}
            done={chat.done}
            onSend={chat.sendMessage}
            onReset={chat.reset}
          />
        ) : (
          <SmsTestSetup
            isStarting={chat.isLoading}
            startError={chat.startError}
            onStart={chat.start}
          />
        )}
      </div>
    </div>
  );
}

function SmsTestSetup({
  isStarting,
  startError,
  onStart,
}: {
  isStarting: boolean;
  startError: string | null;
  onStart: (body: StartSmsTestBody) => void;
}): React.ReactElement {
  const [mode, setMode] = useState<Mode>('referral');
  const [documentId, setDocumentId] = useState('');
  // Patient search: one "selected" object OR a free-text term — never a tri-state
  // that could show a label for a different id than what's submitted.
  const [patient, setPatient] = useState<{ id: string; label: string } | null>(null);
  const [patientTerm, setPatientTerm] = useState('');
  const [isNewPatient, setIsNewPatient] = useState(true);

  const scope = useSchedulingScope();

  const { data: docData, isLoading: docsLoading } = useDocuments({
    classification: 'referral',
    limit: 50,
    sortBy: 'documentDate',
    sortOrder: 'desc',
  });
  const referrals = useMemo(() => (docData?.items ?? []).filter((r) => !!r.patientId), [docData]);
  const { data: patientResults } = usePatientSearch(patientTerm);

  const target = mode === 'referral' ? documentId : (patient?.id ?? '');
  const canStart = Boolean(target) && scope.ready && !isStarting;

  const handleStart = (): void => {
    const s = {
      providerId: scope.providerId,
      locationId: scope.locationId,
      appointmentTypeId: scope.appointmentTypeId,
      isNewPatient,
    };
    onStart(
      mode === 'referral'
        ? { mode: 'referral', documentId, ...s }
        : { mode: 'direct', patientId: patient!.id, ...s }
    );
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Mode toggle */}
        <div className="inline-flex rounded-md border bg-muted/30 p-1">
          <ModeButton
            active={mode === 'referral'}
            onClick={() => setMode('referral')}
            label="From referral"
          />
          <ModeButton
            active={mode === 'direct'}
            onClick={() => setMode('direct')}
            label="Pick a patient"
          />
        </div>

        {mode === 'referral' ? (
          <Field
            label="Referral"
            hint="Patient-matched referral documents. Patient + referral context come from it."
          >
            <select
              value={documentId}
              onChange={(e) => setDocumentId(e.target.value)}
              className={selectClass}
            >
              <option value="">{docsLoading ? 'Loading referrals…' : 'Select a referral…'}</option>
              {referrals.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.patientName ?? 'Unknown patient'}
                  {r.referringProviderName ? ` — ref. ${r.referringProviderName}` : ''}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Patient" hint="Search by name and pick a real patient.">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={patient ? patient.label : patientTerm}
                onChange={(e) => {
                  setPatientTerm(e.target.value);
                  setPatient(null);
                }}
                placeholder="Type a patient name…"
                className="pl-8"
              />
            </div>
            {!patient && (patientResults?.items?.length ?? 0) > 0 && (
              <div className="mt-1 max-h-40 overflow-y-auto rounded-md border">
                {patientResults!.items.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setPatient({ id: p.id, label: `${p.firstName} ${p.lastName}`.trim() });
                      setPatientTerm('');
                    }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                  >
                    {p.firstName} {p.lastName}
                  </button>
                ))}
              </div>
            )}
          </Field>
        )}

        <div className="space-y-4">
          <SchedulingScopeFields scope={scope} idPrefix="sms-test" />
        </div>

        <label className="flex cursor-pointer items-start gap-3">
          <Switch checked={isNewPatient} onCheckedChange={setIsNewPatient} className="mt-0.5" />
          <div className="flex flex-col">
            <span className="text-sm font-medium">New patient</span>
            <span className="text-xs text-muted-foreground">
              Drives eligibility + the new-patient reminders in the agent's flow.
            </span>
          </div>
        </label>

        <p className="text-xs text-muted-foreground">
          Provider, location, and appointment type are all required — the agent can only offer slots
          for a fully-scoped provider. Pick a combination that has open slots and eligibility.
        </p>

        {startError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {startError}
          </div>
        )}

        <Button size="lg" onClick={handleStart} disabled={!canStart} className="gap-2">
          <Play className="h-4 w-4" />
          {isStarting ? 'Starting…' : 'Start conversation'}
        </Button>
      </div>
    </div>
  );
}

const selectClass =
  'block w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {children}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1.5 text-xs font-medium transition-colors',
        active ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {label}
    </button>
  );
}
